<template>
  <div>
    <!-- TODO kunna välja organizationGroup om det inte redan är valt i requestSession -->
    <option-label v-if="showCause" :title="t('donationDetails.form.donationCauseId.label')" class="mb-2" />
    <v-autocomplete
      v-if="showCause"
      v-model="donationDetails.donationCause"
      data-cy="basic-donation-details-autocomplete"
      density="comfortable"
      :placeholder="t('donationDetails.form.donationCauseId.placeholder')"
      persistent-placeholder
      :hint="t('donationDetails.form.donationCauseId.hint')"
      :allow-overflow="false"
      :menu-props="{ closeOnContentClick: true }"
      :items="activeDonationCauses"
      item-title="name"
      return-object
      :rules="[rules.required]"
      required
    >
      <template #item="{ item, props }">
        <v-list-item v-bind="props">
          <v-list-item-title>
            {{ item.raw.name }}
            <span
              v-if="item.raw.bookingAccount && requestSession.organizationGroup"
              class="font-weight-medium"
            >
              | {{ requestSession.organizationGroup.giro }}</span
            >
          </v-list-item-title>
          <v-list-item-subtitle v-if="requestSession.organizationGroup">
            {{ requestSession.organizationGroup.name }}
          </v-list-item-subtitle>
        </v-list-item>
      </template>
    </v-autocomplete>
  </div>
</template>

<script lang="ts">
import { defineComponent, computed, ref, watch } from 'vue';
import store from '@/store';
import { RequestSession } from '@/interfaces/requestSession';
import { Donation } from '@/interfaces/donation/donation';
import { stepV1, stepV2 } from '@/enums/donationStepOrder';
import { DonationCause } from '@/interfaces/organizationgroup/donationCause';
import { DonationStep } from '@/interfaces/donationStep';
import { isMemoryGift } from '@/classes/DonationType';
import { DonationGuideVersion } from '@/enums/donationGuideVersion';
import { useValidationRules } from '@/composables/useValidationRules';
import { useLabel } from '@/composables/useLabel';
import OptionLabel from '@/components/OptionLabel.vue';

export default defineComponent({
  components: { OptionLabel },
  props: {
    value: {
      type: Object as () => Donation,
      default: () => ({}) as Donation,
    },
    form: {
      type: Object,
      default: null,
    },
  },
  emits: ['update:value', 'addStep', 'removeStep', 'previousStep', 'nextStep'],

  setup(props, { emit }) {
    // using top-level store import rather than instance proxy

    const showCause = ref(false);

    const steps = computed(() => {
      return getDonationStepFlowVersion() == DonationGuideVersion.V1 ? stepV1 : stepV2;
    });

    const donationDetails = computed({
      get(): Donation {
        return props.value; // Assuming it's Donation
      },
      set(val: Donation) {
        emit('update:value', val);
      },
    });

    const requestSession = computed(() => store.state.requestSession as RequestSession);

    const donation = computed(() => store.state.donation as Donation);

    const currentStep = computed(() => store.state.currentStep);

    const activeDonationCauses = computed(() => {
      return requestSession.value.organizationGroup.donationCauses.filter(
        (donationCause: DonationCause) => {
          return donationCause.active;
        }
      );
    });

    const { t } = useLabel();
    const { rules } = useValidationRules(t);

    // Inlined StepperFlowMixin methods
    const getDonationStepFlowVersion = () => {
      return store.state.donationGuideVersion || DonationGuideVersion.V1;
    };

    const calculateShowCause = () => {
      // TODO - must be handled different if we don't have an OrganizationGroup in our requestSession
      if (activeDonationCauses.value && activeDonationCauses.value.length === 1) {
        showCause.value = false;
        donationDetails.value.donationCause = activeDonationCauses.value[0];
      } else {
        showCause.value = true;
      }
    };

    const handleStepRemoval = (oldVal: number | DonationStep | null | undefined) => {
      const targetStep =
        getDonationStepFlowVersion() === DonationGuideVersion.V1
          ? stepV1.DONATION_DETAILS
          : stepV2.CERTIFICATE_DETAILS;
      emit('removeStep', targetStep);
      if (oldVal === targetStep + 1) {
        emit('previousStep');
      } else {
        emit('nextStep');
      }
    };

    watch(currentStep, (newVal, oldVal) => {
      const newStepNumber = typeof newVal === 'number' ? newVal : (newVal as DonationStep)?.step;
      const targetStep =
        getDonationStepFlowVersion() === DonationGuideVersion.V1
          ? stepV1.DONATION_DETAILS
          : stepV2.CERTIFICATE_DETAILS;

      if (newStepNumber === targetStep) {
        calculateShowCause();
        // TODO - handle if we have more than one OrganizationGroup
        if (!showCause.value) {
          // if there is only one cause we skip this stepV1
          handleStepRemoval(oldVal);
        }
      } else if (!isMemoryGift(donation.value.donationType.type)) {
        emit('addStep', targetStep);
      }
    });

    return {
      showCause,
      steps,
      donationDetails,
      requestSession,
      donation,
      currentStep,
      activeDonationCauses,
      rules,
      t,
    };
  },
});
</script>
